import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  AnimatePresence, motion, useAnimation, useDragControls, useMotionValue, useReducedMotion,
} from "framer-motion";
import {
  FiArrowUpRight, FiCalendar, FiCheck, FiChevronDown, FiClock, FiCreditCard, FiFileText, FiMaximize2, FiMic,
  FiMinimize2, FiMinus, FiPercent, FiRefreshCw, FiSend, FiSquare, FiUsers, FiVolume2, FiX,
} from "react-icons/fi";
import { RiArrowLeftLine, RiChatNewLine, RiHistoryLine } from "react-icons/ri";
import AulasAIMark from "./AulasAIMark";
import {
  ASSISTANT_BASE, answerAction, getConversation, latestConversation, recentChats, sendMessage,
} from "./assistantApi";
import { useDictation, useSpeaker } from "./useVoice";
import "./aulasAI.css";

// Screens where the button stays out of the way: live classes and tests being taken.
const HIDDEN_ON = ["/broadcast", "/liveclass", "/liveplayer", "/student-test/questions", "/practice-testScreen",
  "/practiseTest-questionScreen", "/test-stud-submit-ques-screen"];
// Screens with their own control in the bottom-right corner: the button sits above it (px from the bottom).
const RAISED_ON = {
  "/dashboard-student": 88, // the aulas care chat icon
  "/user-management/students-by-courses": 84, // pagination
  "/fee-management/all-students/courses/view-all": 100, // pagination
};
const BASE_BOTTOM = 24;
const RIGHT = 24;
const DOCKED = { width: 400, height: 640 };

const SUGGESTIONS = {
  student: [
    { icon: FiCalendar, label: "My classes today", ask: "What are my classes today?" },
    { icon: FiPercent, label: "My attendance", ask: "What is my attendance this month?" },
    { icon: FiCreditCard, label: "My fees", ask: "Do I have any fee due?" },
    { icon: FiFileText, label: "Upcoming tests", ask: "Do I have any upcoming tests?" },
  ],
  teacher: [
    { icon: FiCalendar, label: "My classes today", ask: "What are my classes today?" },
    { icon: FiFileText, label: "Tests today", ask: "Which tests are there today?" },
    { icon: FiClock, label: "My leave balance", ask: "What is my leave balance?" },
  ],
  admin: [
    { icon: FiUsers, label: "Staff attendance today", ask: "How is the staff attendance today?" },
    { icon: FiCreditCard, label: "Fee overview", ask: "Give me the fee overview." },
    { icon: FiCalendar, label: "Classes today", ask: "How many classes are scheduled today?" },
    { icon: FiFileText, label: "Tests today", ask: "Which tests are there today?" },
  ],
};

const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);

function suggestionsFor(user) {
  const roles = [...asList(user?.role), ...asList(user?.privilage)];
  if (roles.some((r) => ["SuperAdmin", "Admin", "Super Admin"].includes(r))) return SUGGESTIONS.admin;
  if (roles.includes("Student")) return SUGGESTIONS.student;
  return SUGGESTIONS.teacher;
}

function friendly(error) {
  if (error?.status === 401) return "Your session has ended. Please log in again to keep chatting.";
  if (error?.status === 409) return "I'm still working on your last message. Give me a moment.";
  if (error?.status === 503) return "aulasAI is busy right now. Please try again in a minute.";
  if (error instanceof TypeError) return "I couldn't reach aulasAI. Check your connection and try again.";
  return error?.message || "Something went wrong. Please try again.";
}

let nextKey = 1;
const newKey = () => `m${nextKey++}`;
const fromServer = (m) => ({
  key: newKey(), role: m.role, text: m.text, at: m.at, links: m.links || [],
  pending: m.pending_action || null, decided: m.decided || null, quick: m.quick_replies || [],
});
const timeOf = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return isNaN(d) ? "" : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};
const ago = (iso) => {
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (isNaN(minutes)) return "";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ago`;
};
const isPhone = () => window.innerWidth <= 600;

// How the panel arrives and leaves: minimise glides gently into the button, close drops away.
// x / y are where the panel sits (it can be dragged), dx / dy the button's position relative to that.
const panelMotion = {
  shown: ({ x = 0, y = 0 }) => ({
    opacity: 1, scale: 1, x, y, transition: { type: "spring", stiffness: 340, damping: 30 },
  }),
  gone: ({ mode, x = 0, y = 0, dx = 0, dy = 0, reduce }) => {
    if (reduce) return { opacity: 0, transition: { duration: 0.15 } };
    if (mode === "close") {
      return { opacity: 0, x, y: y + 40, scale: 0.94, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } };
    }
    return {
      opacity: 0, scale: 0.3, x: x + dx, y: y + dy,
      transition: { duration: 0.34, ease: [0.4, 0, 0.2, 1], opacity: { duration: 0.3, ease: "easeIn" } },
    };
  },
};

/** The user's recent chats: used by the toolbar's "Recent chats" view and by the sidebar when maximised. */
const RecentList = ({ recent, error, currentId, busy, reduceMotion, onOpen, onNew, compact }) => (
  <div className="aai-recent-list">
    {recent === null ? (
      [0, 1, 2].map((i) => <div key={i} className="aai-recent-skeleton" />)
    ) : recent.length === 0 ? (
      <div className="aai-recent-empty">
        <RiHistoryLine />
        <p>{error || "No chats in the past hour yet."}</p>
        {!error && !compact && (
          <button type="button" className="aai-btn aai-btn-primary" onClick={onNew}>
            <RiChatNewLine /> Start a new chat
          </button>
        )}
      </div>
    ) : (
      recent.map((chat, i) => (
        <motion.button
          type="button"
          key={chat.conversation_id}
          className={`aai-recent-item ${chat.conversation_id === currentId ? "aai-recent-current" : ""}`}
          onClick={() => onOpen(chat)}
          disabled={busy}
          initial={reduceMotion ? false : { opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.05 * i }}
        >
          <div className="aai-recent-title">{chat.title}</div>
          <div className="aai-recent-meta">
            <span className="aai-recent-preview">{chat.preview}</span>
            <span className="aai-recent-time">{ago(chat.last_active)}</span>
          </div>
        </motion.button>
      ))
    )}
    {recent && recent.length > 0 && error && <div className="aai-recent-error">{error}</div>}
  </div>
);

const AulasAI = ({ user, authenticated }) => {
  const history = useHistory();
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const [open, setOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [unread, setUnread] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [view, setView] = useState("chat"); // "chat" or "recent" (the toolbar's list, normal size only)
  const [recent, setRecent] = useState(null); // null = loading
  const [recentError, setRecentError] = useState("");
  const [motionInfo, setMotionInfo] = useState({ mode: "minimize", x: 0, y: 0, dx: 0, dy: 0 }); // arrive / leave
  const launcher = useAnimation(); // the button's entrance, and its bounce when it catches a minimised panel
  const drag = useDragControls();
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);
  const offsetRef = useRef({ x: 0, y: 0 }); // where the panel was moved to
  const screenRef = useRef(null); // the area the panel may be moved within

  const listRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const openRef = useRef(open);
  const abortRef = useRef(null);
  const restoredRef = useRef(false);
  openRef.current = open;

  // Voice: what is heard goes after whatever was already typed; nothing is sent until the user presses Send.
  const typedRef = useRef("");
  const dictation = useDictation((heard) => setInput(typedRef.current ? `${typedRef.current} ${heard}` : heard));
  const speaker = useSpeaker();
  const voiceNote = dictation.error || speaker.error;
  const { stop: stopListening, clearError: clearDictationError } = dictation;
  const { stop: stopSpeaking, clearError: clearSpeakerError } = speaker;

  const userKey = authenticated && user?.id ? `${user.id}` : null;
  const bottom = RAISED_ON[location.pathname] || BASE_BOTTOM;
  const hidden = !ASSISTANT_BASE || !userKey || HIDDEN_ON.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!hidden) launcher.start({ opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 380, damping: 30 } });
  }, [hidden, launcher]);

  // The small lift on hover (the hover itself is caught by the still spot around the button).
  useEffect(() => {
    if (!hidden && !reduceMotion) launcher.start({ y: hovered ? -2 : 0, transition: { duration: 0.15 } });
  }, [hovered, hidden, reduceMotion, launcher]);

  const resetChat = useCallback(() => {
    abortRef.current && abortRef.current.abort();
    stopListening();
    stopSpeaking();
    setMessages([]);
    setConversationId(null);
    setBusy(false);
    setInput("");
    setView("chat");
  }, [stopListening, stopSpeaking]);

  // Closed or minimised: stop listening and reading.
  useEffect(() => {
    if (!open) {
      stopListening();
      stopSpeaking();
    }
  }, [open, stopListening, stopSpeaking]);

  // A voice message goes away by itself after a few seconds.
  useEffect(() => {
    if (!voiceNote) return undefined;
    const timer = setTimeout(() => {
      clearDictationError();
      clearSpeakerError();
    }, 6000);
    return () => clearTimeout(timer);
  }, [voiceNote, clearDictationError, clearSpeakerError]);

  // Logged out, or someone else logged in: nothing from the previous chat stays on screen.
  useEffect(() => {
    resetChat();
    setOpen(false);
    setMaximized(false);
    setUnread(false);
    setRecent(null);
    restoredRef.current = false;
  }, [userKey, resetChat]);

  // First time it opens: carry on the user's conversation from the last hour, if there is one.
  useEffect(() => {
    if (!open || restoredRef.current || !userKey) return;
    restoredRef.current = true;
    latestConversation()
      .then((t) => {
        if (!t || !t.messages?.length) return;
        setMessages((now) => (now.length ? now : t.messages.map(fromServer)));
        setConversationId((now) => now || t.conversation_id);
      })
      .catch(() => {});
  }, [open, userKey]);

  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 300);
    }
  }, [open]);

  useEffect(() => {
    if (atBottom && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, atBottom, view, maximized]);

  const loadRecent = useCallback(() => {
    setRecentError("");
    recentChats(5)
      .then(setRecent)
      .catch((error) => {
        setRecent((now) => now || []);
        setRecentError(friendly(error));
      });
  }, []);

  // The sidebar of the maximised window always lists the recent chats.
  useEffect(() => {
    if (open && maximized) loadRecent();
  }, [open, maximized, loadRecent]);

  // ---------------------------------------------------------------- opening, closing and moving
  // From the centre of the panel (where it sits now) to the centre of the button: where minimise goes.
  const towardsButton = (max) => {
    const { x, y } = offsetRef.current;
    const button = launcherRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!button) return { x, y, dx: 0, dy: 0 };
    let cx; let cy;
    if (panel) {
      cx = panel.left + panel.width / 2;
      cy = panel.top + panel.height / 2;
    } else { // not on screen yet: where it will be
      cx = (max ? window.innerWidth / 2 : window.innerWidth - RIGHT - DOCKED.width / 2) + x;
      cy = (max ? window.innerHeight / 2
        : window.innerHeight - (bottom + 64) - Math.min(DOCKED.height, window.innerHeight - 120) / 2) + y;
    }
    return { x, y, dx: button.left + button.width / 2 - cx, dy: button.top + button.height / 2 - cy };
  };

  const openPanel = () => {
    setMotionInfo({ mode: "minimize", ...towardsButton(maximized) }); // comes out of the button
    setOpen(true);
  };

  const minimize = () => {
    setMotionInfo({ mode: "minimize", ...towardsButton(maximized) });
    setOpen(false);
  };

  const close = () => {
    setMotionInfo({ mode: "close", ...offsetRef.current, dx: 0, dy: 0 });
    setOpen(false);
  };

  // Moving: the dark header is the handle. Where it was left is kept while minimised.
  const startDrag = (event) => {
    if (isPhone() || event.target.closest("button")) return;
    drag.start(event);
  };
  const endDrag = () => {
    offsetRef.current = { x: dragX.get(), y: dragY.get() };
  };
  const resetPosition = () => {
    offsetRef.current = { x: 0, y: 0 };
    dragX.set(0);
    dragY.set(0);
  };

  const onExitComplete = () => {
    if (motionInfo.mode === "close") {
      resetChat(); // close: the next open starts fresh (this chat stays in Recent chats), in its usual place
      setMaximized(false);
      resetPosition();
    } else if (!reduceMotion) {
      // minimise: the button catches the panel
      launcher.start({ scale: [1, 1.14, 0.95, 1], transition: { duration: 0.45, times: [0, 0.35, 0.7, 1] } });
    }
  };

  const toggleMaximize = () => {
    resetPosition(); // the big window opens centred, and the small one goes back to its corner
    setMaximized((m) => !m);
    setView("chat");
  };

  // ---------------------------------------------------------------- talking
  const update = (msgKey, change) =>
    setMessages((all) => all.map((m) => (m.key === msgKey ? { ...m, ...(typeof change === "function" ? change(m) : change) } : m)));

  // Streams one assistant reply into a new bubble; `request` makes the call with the stream handlers.
  const reply = useCallback(async (request) => {
    const msgKey = newKey();
    setMessages((all) => [...all, { key: msgKey, role: "assistant", text: "", status: "", streaming: true, links: [] }]);
    setBusy(true);
    setAtBottom(true);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const done = await request({
        signal: controller.signal,
        onStatus: (status) => update(msgKey, { status }),
        onDelta: (text) => update(msgKey, (m) => ({ text: m.text + text, status: "" })),
      });
      setConversationId(done.conversation_id);
      update(msgKey, {
        text: done.reply, links: done.links || [], pending: done.pending_action || null, decided: null,
        quick: done.quick_replies || [], streaming: false, status: "", at: new Date().toISOString(),
      });
      if (!openRef.current) setUnread(true);
    } catch (error) {
      if (error.name === "AbortError") {
        setMessages((all) => all.filter((m) => m.key !== msgKey));
      } else {
        if (error.status === 404) setConversationId(null); // that chat expired: the next message starts a new one
        update(msgKey, { role: "error", text: friendly(error), streaming: false, status: "", retry: request });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  // Keep the maximised sidebar current after each reply.
  useEffect(() => {
    if (open && maximized && !busy && conversationId) loadRecent();
  }, [busy, conversationId, open, maximized, loadRecent]);

  const send = (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    stopListening();
    setInput("");
    setMessages((all) => [...all, { key: newKey(), role: "user", text: message, at: new Date().toISOString() }]);
    reply((handlers) => sendMessage(message, conversationId, handlers));
  };

  const decide = (msg, approve) => {
    if (busy || !conversationId) return;
    update(msg.key, { decided: approve ? "approved" : "rejected" });
    setMessages((all) => [...all, { key: newKey(), role: "user", text: approve ? "Yes, go ahead" : "No", at: new Date().toISOString() }]);
    reply((handlers) => answerAction(conversationId, msg.pending.id, approve, handlers));
  };

  const retry = (msg) => {
    if (busy || !msg.retry) return;
    setMessages((all) => all.filter((m) => m.key !== msg.key));
    reply(msg.retry);
  };

  const newChat = () => {
    resetChat();
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
  };

  const showRecent = () => {
    setView("recent");
    setRecent(null);
    loadRecent();
  };

  const openChat = (chat) => {
    if (busy) return;
    if (chat.conversation_id === conversationId) {
      setView("chat");
      return;
    }
    getConversation(chat.conversation_id)
      .then((t) => {
        if (!t) {
          setRecentError("That chat has expired.");
          return;
        }
        setMessages(t.messages.map(fromServer));
        setConversationId(t.conversation_id);
        setAtBottom(true);
        setView("chat");
      })
      .catch((error) => setRecentError(friendly(error)));
  };

  const openLink = (link) => {
    if (/^https?:\/\//.test(link.path)) {
      window.open(link.path, "_blank", "noopener,noreferrer");
      return;
    }
    history.push(link.path);
    if (isPhone()) setOpen(false); // full-screen on phones: show the page
    else if (maximized) setMaximized(false); // let the screen it opened be seen
  };

  const toggleMic = () => {
    if (dictation.listening) {
      dictation.stop();
      return;
    }
    stopSpeaking(); // don't let the assistant's voice be typed in
    typedRef.current = input.trim();
    dictation.start();
    inputRef.current && inputRef.current.focus();
  };

  const toggleRead = (m) => (speaker.speaking === m.key ? speaker.stop() : speaker.speak(m.key, m.text));

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onScroll = () => {
    const el = listRef.current;
    if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 60);
  };

  if (hidden) return null;

  const firstName = (user?.name || "").trim().split(/\s+/)[0];
  const lastPending = [...messages].reverse().find((m) => m.pending && m.role === "assistant");
  const custom = { ...motionInfo, reduce: reduceMotion };

  const recentList = (compact) => (
    <RecentList recent={recent} error={recentError} currentId={conversationId} busy={busy} reduceMotion={reduceMotion}
                onOpen={openChat} onNew={newChat} compact={compact} />
  );

  const chatArea = (
    <>
      <div className="aai-body" ref={listRef} onScroll={onScroll}>
        {messages.length === 0 ? (
          <div className="aai-welcome">
            <motion.div
              animate={reduceMotion ? {} : { y: [0, -6, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            >
              <AulasAIMark size={68} />
            </motion.div>
            <h3>Hi{firstName ? ` ${firstName}` : ""} 👋</h3>
            <p>I can check your classes, attendance, fees, tests and more. What would you like to know?</p>
            <div className="aai-chips">
              {suggestionsFor(user).map(({ icon: Icon, label, ask }, i) => (
                <motion.button
                  type="button"
                  key={label}
                  className="aai-chip"
                  onClick={() => send(ask)}
                  initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1 }}
                >
                  <Icon /> {label}
                </motion.button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <motion.div
              key={m.key}
              className={`aai-row aai-row-${m.role}`}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22 }}
            >
              {m.role !== "user" && <div className="aai-avatar"><AulasAIMark size={26} /></div>}
              <div className="aai-stack">
                <div className={`aai-bubble aai-bubble-${m.role}`} title={timeOf(m.at)}>
                  {m.streaming && !m.text ? (
                    <div className="aai-typing">
                      <span className="aai-dots"><i /><i /><i /></span>
                      {m.status && <span className="aai-status">{m.status}</span>}
                    </div>
                  ) : (
                    <div className="aai-text">
                      {m.text}
                      {m.streaming && <span className="aai-caret" />}
                    </div>
                  )}
                  {m.streaming && m.text && m.status && <div className="aai-status aai-status-inline">{m.status}</div>}

                  {m.pending && (
                    m.decided ? (
                      <div className={`aai-decided aai-decided-${m.decided}`}>
                        {m.decided === "approved" ? <><FiCheck /> Done</> : "Cancelled"}
                      </div>
                    ) : m === lastPending && !busy ? (
                      <div className="aai-actions">
                        <button type="button" className="aai-btn aai-btn-primary" onClick={() => decide(m, true)}>
                          <FiCheck /> Yes, go ahead
                        </button>
                        <button type="button" className="aai-btn aai-btn-ghost" onClick={() => decide(m, false)}>
                          No
                        </button>
                      </div>
                    ) : null
                  )}

                  {m.role === "error" && m.retry && (
                    <button type="button" className="aai-btn aai-btn-ghost aai-retry" onClick={() => retry(m)}>
                      <FiRefreshCw /> Try again
                    </button>
                  )}
                </div>
                {m.role === "assistant" && !m.streaming && m.text && (
                  <button
                    type="button"
                    className={`aai-read ${speaker.speaking === m.key ? "aai-read-on" : ""}`}
                    onClick={() => toggleRead(m)}
                    title={speaker.speaking === m.key ? "Stop reading" : "Read aloud"}
                    aria-label={speaker.speaking === m.key ? "Stop reading" : "Read aloud"}
                  >
                    {speaker.speaking === m.key ? <><FiSquare /> Stop</> : <FiVolume2 />}
                  </button>
                )}
                {m.links && m.links.length > 0 && (
                  <div className="aai-links">
                    {m.links.map((l) => (
                      <button type="button" key={l.path} className="aai-link" onClick={() => openLink(l)}>
                        {l.label} <FiArrowUpRight />
                      </button>
                    ))}
                  </div>
                )}
                {/* ready answers ("Use defaults"): only under the latest reply, and a tap sends them */}
                {m.quick && m.quick.length > 0 && m === messages[messages.length - 1] && !busy && (
                  <div className="aai-links">
                    {m.quick.map((q) => (
                      <button type="button" key={q} className="aai-link aai-quick" onClick={() => send(q)}>
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))
        )}
      </div>

      <AnimatePresence>
        {!atBottom && (
          <motion.button
            type="button"
            className="aai-jump"
            onClick={() => setAtBottom(true)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            aria-label="Jump to latest"
          >
            <FiChevronDown />
          </motion.button>
        )}
      </AnimatePresence>

      <footer className="aai-footer">
        {voiceNote && <div className="aai-voice-note" role="status">{voiceNote}</div>}
        <div className="aai-composer">
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            maxLength={2000}
            placeholder={dictation.listening ? "Listening… speak now" : "Ask aulasAI anything…"}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            aria-label="Message aulasAI"
          />
          <button type="button" className={`aai-mic ${dictation.listening ? "aai-mic-on" : ""}`} onClick={toggleMic}
                  title={dictation.listening ? "Stop voice typing" : "Voice typing (English)"}
                  aria-label={dictation.listening ? "Stop voice typing" : "Voice typing"}
                  aria-pressed={dictation.listening}>
            <FiMic />
          </button>
          <button type="button" className="aai-send" onClick={() => send()} disabled={busy || !input.trim()}
                  title="Send" aria-label="Send">
            <FiSend />
          </button>
        </div>
        <div className="aai-disclaimer">aulasAI can make mistakes. Please check important details.</div>
      </footer>
    </>
  );

  return (
    <div className="aai-root" onKeyDown={(e) => e.key === "Escape" && open && minimize()}>
      <AnimatePresence>
        {open && maximized && (
          <motion.div
            key="backdrop"
            className="aai-backdrop"
            onClick={minimize}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      <div ref={screenRef} className="aai-screen" aria-hidden="true" />
      <AnimatePresence custom={custom} onExitComplete={onExitComplete}>
        {open && (
          <motion.section
            key="panel"
            ref={panelRef}
            className={`aai-panel ${maximized ? "aai-panel-max" : ""}`}
            role="dialog"
            aria-label="aulasAI chat"
            style={{ x: dragX, y: dragY, ...(maximized ? {} : { bottom: bottom + 64 }) }}
            custom={custom}
            variants={panelMotion}
            initial="gone"
            animate="shown"
            exit="gone"
            layout
            drag
            dragControls={drag}
            dragListener={false}
            dragMomentum={false}
            dragElastic={0}
            dragConstraints={screenRef}
            onDragEnd={endDrag}
          >
            <header className="aai-header" onPointerDown={startDrag} title="Drag to move">
              <div className="aai-header-brand">
                <AulasAIMark size={36} />
                <div>
                  <div className="aai-wordmark aai-wordmark-dark">aulas<span>AI</span></div>
                  <div className="aai-subtitle">
                    <span className={`aai-dot ${busy ? "aai-dot-busy" : ""}`} />
                    {busy ? "Working on it…" : "Your aulas assistant"}
                  </div>
                </div>
              </div>
              <div className="aai-lights">
                <button type="button" className="aai-light aai-light-min" onClick={minimize}
                        title="Minimise" aria-label="Minimise">
                  <FiMinus />
                </button>
                <button type="button" className="aai-light aai-light-max" onClick={toggleMaximize}
                        title={maximized ? "Restore size" : "Maximise"} aria-label={maximized ? "Restore size" : "Maximise"}>
                  {maximized ? <FiMinimize2 /> : <FiMaximize2 />}
                </button>
                <button type="button" className="aai-light aai-light-close" onClick={close}
                        title="Close" aria-label="Close and start fresh next time">
                  <FiX />
                </button>
              </div>
            </header>

            {maximized ? (
              <div className="aai-split">
                <aside className="aai-sidebar">
                  <button type="button" className="aai-sidebar-new" onClick={newChat}>
                    <RiChatNewLine /> New chat
                  </button>
                  <div className="aai-sidebar-label">Recent</div>
                  {recentList(true)}
                  <div className="aai-recent-note"><FiClock /> Chats from the past hour only</div>
                </aside>
                <div className="aai-main">{chatArea}</div>
              </div>
            ) : (
              <>
                <div className="aai-toolbar">
                  {view === "recent" ? (
                    <button type="button" className="aai-tool" onClick={() => setView("chat")}>
                      <RiArrowLeftLine /> Back to chat
                    </button>
                  ) : (
                    <button type="button" className="aai-tool" onClick={showRecent}>
                      <RiHistoryLine /> Recent chats
                    </button>
                  )}
                  <button type="button" className="aai-tool" onClick={newChat}>
                    <RiChatNewLine /> New chat
                  </button>
                </div>
                {view === "recent" ? (
                  <div className="aai-recent">
                    {recentList(false)}
                    <div className="aai-recent-note"><FiClock /> Chats from the past hour only</div>
                  </div>
                ) : (
                  chatArea
                )}
              </>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* The hover is caught by this still spot, not by the button: the button lifts on hover, and if it were
          its own hover area, a pointer at its bottom edge would lose it, it would drop back, and so on. */}
      <div className="aai-launcher-spot" style={{ bottom }}
           onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <motion.button
        ref={launcherRef}
        type="button"
        className={`aai-launcher ${open ? "aai-launcher-open" : ""}`}
        onClick={() => (open ? minimize() : openPanel())}
        aria-label={open ? "Minimise aulasAI" : "Chat with aulasAI"}
        aria-expanded={open}
        layout
        initial={reduceMotion ? false : { opacity: 0, y: 20, scale: 0.9 }}
        animate={launcher}
        whileTap={{ scale: 0.96 }}
      >
        {open ? (
          <FiChevronDown className="aai-launcher-close" />
        ) : (
          <>
            <motion.span layout className="aai-launcher-mark"><AulasAIMark size={30} /></motion.span>
            <motion.span layout className="aai-launcher-label">
              {hovered ? <>Chat with aulas<span>AI</span></> : <>aulas<span>AI</span></>}
            </motion.span>
            {unread && <span className="aai-unread" aria-label="New reply" />}
          </>
        )}
      </motion.button>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.session.user,
  authenticated: state.session.authenticated,
});

export default connect(mapStateToProps)(AulasAI);
